<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import UserService from '@/services/UserService'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FloatingInput from '@/components/ui/FloatingInput.vue'
import Spinner from '@/components/ui/Spinner.vue'
import PasswordRequirementList from '@/views/AppManage/PasswordRequirementList.vue'
import { track, EVENTS } from '@/lib/tracking'

const route = useRoute()
const { notify } = useNotification()

const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const processing = ref(false)
const submitted = ref(false)
const success = ref(false)
const linkError = ref('')
const submitError = ref('')
const touched = ref({
	password: false,
	confirmPassword: false,
})

const resetToken = computed(() => String(route.query.resetToken || '').trim())

function getPasswordChecks(value) {
	const next = value || ''
	return {
		minLength: next.length >= 8,
		hasNumber: /\d/.test(next),
		hasSpecial: /[^A-Za-z0-9]/.test(next),
	}
}

function allPasswordChecksMet(checks) {
	return checks.minLength && checks.hasNumber && checks.hasSpecial
}

const passwordChecks = computed(() => getPasswordChecks(password.value))

const passwordHelper = computed(() => {
	if (!touched.value.password && !submitted.value) return ''
	if (!password.value.trim()) return 'Enter a new password.'
	if (!allPasswordChecksMet(passwordChecks.value)) return 'Password does not meet the requirements below.'
	return ''
})

const confirmHelper = computed(() => {
	if (!touched.value.confirmPassword && !submitted.value) return ''
	if (!confirmPassword.value.trim()) return 'Confirm your new password.'
	if (password.value !== confirmPassword.value) return 'Passwords do not match.'
	return ''
})

const showPasswordRequirements = computed(() => (
	!!password.value || touched.value.password || submitted.value
))

const canSubmit = computed(() => (
	!processing.value
	&& !linkError.value
	&& !!password.value.trim()
	&& !!confirmPassword.value.trim()
	&& allPasswordChecksMet(passwordChecks.value)
	&& password.value === confirmPassword.value
))

function isInvalidResetLinkError(err) {
	const message = String(err?.message || '').toLowerCase()
	const code = Number(err?.code)
	return [400, 401, 404, 410].includes(code)
		|| message.includes('token')
		|| message.includes('expired')
		|| message.includes('invalid')
		|| message.includes('already')
		|| message.includes('used')
}

function markTouched(field) {
	touched.value[field] = true
	submitError.value = ''
}

async function requestReset() {
	submitted.value = true
	submitError.value = ''

	if (!resetToken.value) {
		linkError.value = 'This reset link is missing or incomplete.'
		return
	}

	if (!canSubmit.value) return

	processing.value = true
	try {
		await UserService.validatePasswordReset(
			resetToken.value,
			password.value,
			confirmPassword.value,
		)

		track(EVENTS.PASSWORD_RESET_COMPLETED, { method: 'email_link' })
		notify({ group: 'success', text: 'Password has been changed successfully.' })
		success.value = true
	} catch (err) {
		if (isInvalidResetLinkError(err)) {
			linkError.value = err?.message || 'This reset link is invalid, expired, or has already been used.'
		} else {
			submitError.value = err?.message || 'Something went wrong. Please try again.'
		}
	} finally {
		processing.value = false
	}
}

onMounted(() => {
	document.title = 'Reset password – Castr'
	if (!resetToken.value) {
		linkError.value = 'This reset link is missing or incomplete.'
	}
})
</script>

<template>
	<div class="auth-page">
		<div class="auth-card">
			<div class="flex flex-col items-center text-center mb-7">
				<div class="auth-logo-wrapper">
					<div class="auth-logo-dot" aria-hidden="true" />
					<img class="auth-logo" src="@/assets/castr.svg" alt="Castr" />
				</div>
			</div>

			<div v-if="linkError" class="text-center">
				<h1 class="auth-title">Reset link unavailable</h1>
				<p class="auth-subtitle mb-4">
					This password reset link is invalid, expired, or has already been used.
				</p>

				<Alert type="warning" class="mb-5 text-left">
					{{ linkError }}
				</Alert>

				<div class="flex flex-col items-center gap-3">
					<FormButton
						type="main"
						size="lg"
						class="!h-10 !px-8 w-full sm:w-auto"
						to="/forgot-password"
					>
						Request a new link
					</FormButton>
					<router-link to="/signin" class="auth-link text-sm">
						Go to Sign in instead
					</router-link>
				</div>
			</div>

			<div v-else-if="success" class="text-center">
				<h1 class="auth-title">Password updated</h1>
				<p class="auth-subtitle mb-4">
					Your password has been reset. You can sign in with your new password now.
				</p>

				<Alert type="normal" class="mb-5 text-left">
					<div>
						<p class="mb-1 text-sm font-medium text-surface-9">You&apos;re all set</p>
						<p class="mb-0 text-tiny text-surface-7">
							For your security, use your new password the next time you sign in.
						</p>
					</div>
				</Alert>

				<FormButton
					type="main"
					size="lg"
					class="!h-10 !px-8 w-full sm:w-auto"
					to="/signin"
				>
					Go to Sign in
				</FormButton>
			</div>

			<template v-else>
				<div class="mb-6 text-center">
					<h1 class="auth-title">Reset password</h1>
					<p class="auth-subtitle">
						Choose a strong new password for your account.
					</p>
				</div>

				<Alert
					v-if="submitError"
					type="error"
					:text="submitError"
					class="mb-4"
				/>

				<form @submit.prevent="requestReset">
					<div class="mb-4">
						<FloatingInput
							id="reset-password"
							label="New password"
							v-model="password"
							:type="showPassword ? 'text' : 'password'"
							autocomplete="new-password"
							:has-error="!!passwordHelper"
							:helper="passwordHelper"
							@blur="markTouched('password')"
							@update:modelValue="submitError = ''"
						/>
						<div class="flex justify-end mt-1">
							<button
								type="button"
								class="auth-ghost-btn text-tiny"
								@click="showPassword = !showPassword"
							>
								{{ showPassword ? 'Hide password' : 'Show password' }}
							</button>
						</div>
						<PasswordRequirementList
							v-if="showPasswordRequirements"
							:checks="passwordChecks"
						/>
					</div>

					<div class="mb-5">
						<FloatingInput
							id="reset-password-confirm"
							label="Confirm password"
							v-model="confirmPassword"
							:type="showConfirmPassword ? 'text' : 'password'"
							autocomplete="new-password"
							:has-error="!!confirmHelper"
							:helper="confirmHelper"
							@blur="markTouched('confirmPassword')"
							@update:modelValue="submitError = ''"
							@keydown="$event.key === 'Enter' && requestReset()"
						/>
						<div class="flex justify-end mt-1">
							<button
								type="button"
								class="auth-ghost-btn text-tiny"
								@click="showConfirmPassword = !showConfirmPassword"
							>
								{{ showConfirmPassword ? 'Hide password' : 'Show password' }}
							</button>
						</div>
					</div>

					<div class="flex justify-center">
						<FormButton
							type="main"
							size="lg"
							html-type="submit"
							class="!h-10 !px-14 w-full sm:w-auto justify-center"
							:disabled="!canSubmit"
						>
							<template v-if="processing">
								Resetting password
								<Spinner
									text=""
									classes="ml-2"
									spinner-color="currentColor"
									spinner-size="14px"
								/>
							</template>
							<template v-else>Reset password</template>
						</FormButton>
					</div>
				</form>

				<p class="auth-footer mt-6 mb-0 text-center">
					<router-link to="/signin" class="auth-link">Go to Sign in instead</router-link>
				</p>
			</template>
		</div>
	</div>
</template>

<style scoped>
.auth-page {
	position: relative;
	width: 100%;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 16px;
}
.auth-page::before {
	content: '';
	position: absolute;
	inset: 0;
	background:
		radial-gradient(65% 45% at 50% 0%, rgba(0, 98, 255, 0.16), transparent 68%),
		radial-gradient(34% 42% at 50% 46%, rgba(0, 98, 255, 0.07), transparent 72%);
	pointer-events: none;
}
.auth-page > * {
	position: relative;
}
.auth-card {
	width: 100%;
	max-width: 420px;
	background-color: var(--castr-surface);
	border: 1px solid var(--castr-border);
	border-radius: 16px;
	box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.9);
	padding: 40px 28px 32px;
}
@media (min-width: 640px) {
	.auth-card {
		padding: 40px 36px 32px;
	}
}
.auth-logo {
	width: 120px;
	height: 40px;
}
.auth-logo-wrapper {
	position: relative;
	display: inline-block;
	line-height: 0;
}
.auth-logo-dot {
	position: absolute;
	width: 4px;
	height: 4px;
	border-radius: 50%;
	left: 116px;
	top: 12px;
	background-color: #ef0000;
	animation: blink 2s ease-in-out infinite;
}
.auth-title {
	font-family: var(--font-display);
	font-size: 24px;
	line-height: 1.15;
	font-weight: 700;
	letter-spacing: -0.02em;
	color: #fff;
	margin-bottom: 6px;
}
.auth-subtitle {
	font-size: 14px;
	line-height: 1.5;
	color: var(--castr-text-muted);
}
.auth-link {
	color: var(--castr-blue-soft);
	font-weight: 500;
}
.auth-link:hover {
	color: #fff;
	text-decoration: none;
}
.auth-ghost-btn {
	color: var(--castr-text-muted);
}
.auth-ghost-btn:hover {
	color: var(--castr-text);
}
.auth-footer {
	font-size: 14px;
	color: var(--castr-text-muted);
}
.auth-card :deep(button.form-button) {
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0.01em;
	border-radius: 12px !important;
}
.auth-card :deep(button.bg-main:not(:disabled)) {
	box-shadow: 0 10px 30px -8px rgba(0, 98, 255, 0.5);
	transition:
		box-shadow 0.2s ease,
		transform 0.12s ease,
		background-color 0.15s ease;
}
.auth-card :deep(button.bg-main:not(:disabled):hover) {
	transform: translateY(-1px);
	box-shadow: 0 14px 36px -8px rgba(0, 98, 255, 0.6);
}
.auth-card :deep(button.bg-main:not(:disabled):active) {
	transform: translateY(0);
	box-shadow: 0 6px 18px -8px rgba(0, 98, 255, 0.5);
}
.auth-card :deep(button:disabled) {
	opacity: 0.55;
	cursor: not-allowed;
}
</style>

<style>
body:has(.auth-page),
body:has(.auth-page) #app {
	background-color: var(--castr-bg);
}
body:has(.auth-page) .footer span {
	color: var(--castr-text-muted);
}
</style>
